/**
 * Language Context - Provides i18n functionality
 *
 * Following the king-dom pattern from sign-git.
 * Provides `t()` translation function with {{param}} interpolation.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import { translations, type Language, STORAGE_KEY, INTERPOLATION_PATTERN } from '../translations'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem(STORAGE_KEY) as Language) || 'pt'
    }
    return 'pt'
  })

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang)
  }, [])

  // Persist language choice
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language)
    document.documentElement.lang = language
  }, [language])

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const langTranslations = translations[language]
      let translation: string = langTranslations[key] || key

      // Parameter interpolation: {{param}} -> value
      if (params && typeof translation === 'string') {
        translation = translation.replace(
          INTERPOLATION_PATTERN,
          (_, param) => String(params[param] ?? `{{${param}}}`)
        )
      }

      return translation
    },
    [language]
  )

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t]
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}
